import * as React from 'react';
import { ArrowRightIcon } from '@heroicons/react/outline';
import { Icon } from '@vechaiui/react';
import { Link } from 'react-router-dom';
import { useLocalization } from '@fluent/react';

import HomePageBenefitSection from './HomePageBenefitSection';
import MissionCard from './MissionCard';
import SignUpButton from './SignUpButton';
import SignUpButtonGroupPage from './SignUpButtonGroupPage';
import StaticPageLayout from '#/layouts/StaticPageLayout';
import accommodateIcon from '#/assets/static-page/accomodation.png';
import benefitClientIcon from '#/assets/static-page/benefits_clients.svg';
import benefitTransportIcon from '#/assets/static-page/benefits_transporters.svg';
import circleTickIcon from '#/assets/static-page/circle_tick.svg';
import downIcon from '#/assets/static-page/down.svg';
import easyFormIcon from '#/assets/static-page/easy_form.png';
import fairPricesIcon from '#/assets/static-page/fair_prices.png';
import liveTrackIcon from '#/assets/static-page/live_track.png';
import moveLogo from '#/assets/static-page/move_logo.png';
import transporterIcon from '#/assets/static-page/transporter.png';
import { PublicRoute } from '#/constants/page-route';

const HomePage = () => {
  const { l10n } = useLocalization();
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <StaticPageLayout isNavBarDisabled={true}>
      <div>
        <div className="bg-gradient-to-tl from-[#d7f5f1] via-blue-500 to-[#000c69]">
          <div className="flex flex-col justify-between px-12 py-8 gap-y-14">
            <div className="mb-60">
              <img src={moveLogo} />
            </div>
            <div className="flex justify-between">
              <div className="w-full text-white">
                <div className="w-1/2 pb-16 sm:text-2xl lg:text-6xl xl:text-8xl">
                  Your next MOVE in transportation
                </div>
                <div className="w-2/6 text-xl">
                  Introducing the new standardisation of the transportation industry. Book, track,
                  and manage your logistics with just one click.
                </div>
              </div>
              <div className="flex flex-col w-auto px-8 pt-5 bg-white h-max gap-y-6 rounded-3xl">
                <h3 className="font-bold text-blue-700">It’s your move.</h3>
                <div className="text-blue-700">
                  Experience the modernisation of logistics transportation. Sign up now.
                </div>
                <div className="flex flex-col py-10 space-y-4">
                  <SignUpButton
                    className="mt-0 bg-blue-700"
                    iconClassName="text-white"
                    text={l10n.getString('sign-up-client')}
                    textClassName="text-white"
                    route={PublicRoute.CustomerSignup}
                  />
                  <SignUpButton
                    className="mt-0 bg-white border border-blue-700"
                    iconClassName="text-blue-700"
                    text={l10n.getString('sign-up-transporter')}
                    textClassName="text-black"
                    route={PublicRoute.LogisticsSignup}
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-center border-t border-white gap-x-5">
              <div className="py-8 text-white">FIND OUT MORE</div>
              <img src={downIcon} className="w-4" />
            </div>
          </div>
        </div>
        <div className="bg-blue-50 py-28">
          <div className="flex justify-evenly gap-x-10">
            <div className="flex flex-col gap-y-5">
              <div className="flex items-end justify-end gap-x-6">
                <div className="w-1/4 h-40 p-8 bg-white rounded-xl">
                  <img src={fairPricesIcon} className="w-14" />
                  <div className="py-4 text-lg text-gray-500">Fair Prices</div>
                </div>
                <div className="w-1/3 p-8 mr-10 bg-white rounded-xl">
                  <img src={easyFormIcon} className="w-10" />
                  <div className="py-4 text-lg text-gray-500">
                    Easy form fill up and one click bookings with accessible customer service
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-y-8 gap-x-4">
                <div className="flex flex-col gap-y-5">
                  <div className="w-4/5 px-8 py-6 ml-auto bg-white h-44 rounded-xl">
                    <img src={accommodateIcon} className="w-14" />
                    <div className="py-4 text-lg text-gray-500">
                      Accommodates last minute and on-demand bookings
                    </div>
                  </div>
                  <div className="w-1/2 p-8 ml-auto bg-white rounded-xl">
                    <img src={transporterIcon} className="w-14" />
                    <div className="py-4 text-lg text-gray-500">High quality transporters</div>
                  </div>
                </div>
                <div className="w-64 p-8 bg-white rounded-xl h-52">
                  <img src={liveTrackIcon} className="w-14" />
                  <div className="py-4 text-lg text-gray-500">
                    Monitor and live track your transport movements
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-y-10">
              <div className="text-4xl text-blue-700 xl:text-6xl">The change starts with MOVE</div>
              <p className="w-full py-5 text-xl text-gray-400 2xl:w-1/2">
                Skip the hassle and outdated inefficiencies with our one-stop solve-all logistics
                platform.
                <br /> <br />
                Let MOVE help find you the right transporter.
              </p>
              <SignUpButton
                className="bg-blue-700"
                iconClassName="text-white"
                text={l10n.getString('sign-up-now')}
                textClassName="text-white"
                route={PublicRoute.CustomerSignup}
              />
            </div>
          </div>
        </div>
        <HomePageBenefitSection />
        <div className="py-20 bg-blue-50">
          <div className="flex flex-col items-center text-center text-blue-700 gap-y-6">
            <div>WHY MOVE</div>
            <div className="text-6xl">There is no moving back.</div>
            <div className="text-2xl text-gray-400 lg:w-2/6 sm:w-1/2">
              By centralising all stakeholders in a hub, we can guarantee job completions the
              industry has never seen before.
            </div>
          </div>
          <div className="grid w-full grid-cols-2 gap-10 p-10 place-items-center">
            <BenefitCard type="client" />
            <BenefitCard type="transporter" />
          </div>
        </div>
        <div className="py-12 bg-blue-700">
          <div className="flex flex-col items-center mb-12 text-white gap-y-5">
            <p>OUR AIM</p>
            <p className="text-4xl">To standardise the transportation industry</p>
            <p className="text-xl">
              Our mission is to be the go-to transportation service that stems from:
            </p>
          </div>
          <div className="flex justify-center">
            <MissionCard />
          </div>
          <div className="flex flex-col items-center pt-10">
            <Link className="text-white underline underline-offset-2" to={PublicRoute.AboutUs}>
              Learn more about us
            </Link>
          </div>
        </div>
      </div>
      <SignUpButtonGroupPage />
    </StaticPageLayout>
  );
};

const BenefitCard: React.FC<BenefitCardProps> = ({ type }) => {
  return (
    <div className="w-full p-10 bg-white rounded-xl 2xl:w-4/6 3xl:w-1/2">
      <div className="flex flex-col gap-y-10">
        <div className="flex justify-between">
          <div>
            <div className="text-blue-700">
              BENEFITS FOR {type === 'client' ? 'CLIENTS' : 'TRANSPORTERS'}
            </div>
            <div className="py-5 text-4xl text-blue-900">
              {type === 'client'
                ? 'Deliver your goods efficiently'
                : 'Grow your transportation business'}
            </div>
          </div>
          <div>
            <img
              src={type === 'client' ? benefitClientIcon : benefitTransportIcon}
              className={type === 'client' ? 'w-36' : 'w-44'}
            />
          </div>
        </div>
        <div className="flex flex-col gap-y-5">
          <BenefitPoint
            text={type === 'client' ? 'Fair Prices' : 'Fair prices and fairer industry'}
          />
          <BenefitPoint
            text={type === 'client' ? 'High-quality transporters' : 'Screened through clientele'}
          />
          <BenefitPoint
            text={type === 'client' ? 'Easy sign-up forms' : 'Have better chances on round trips'}
          />
          <BenefitPoint
            text={type === 'client' ? 'One-click bookings' : 'Implemented work benefits'}
          />
          <BenefitPoint
            text={
              type === 'client' ? 'Accessible customer service' : 'Monthly reports and analytics'
            }
          />
          <BenefitPoint
            text={
              type === 'client'
                ? 'Accepts on-demand bookings'
                : 'Get reviews & ratings from clients'
            }
          />
          <BenefitPoint
            text={type === 'client' ? 'Live tracking of goods' : 'Build extra sales for free'}
          />
        </div>
        <div>
          <button className="flex items-center justify-between px-5 py-4 bg-blue-700 rounded-full w-72 hover:cursor-pointer">
            <div className="text-white">
              Sign Up as {type === 'client' ? 'Client' : 'Transporter'}
            </div>
            <Icon as={ArrowRightIcon} className="w-5 text-white" label="selector" />
          </button>
        </div>
      </div>
    </div>
  );
};

type BenefitCardProps = {
  type: 'client' | 'transporter';
};

const BenefitPoint: React.FC<BenefitPointProps> = ({ text }) => {
  return (
    <div className="flex items-center gap-x-5">
      <img src={circleTickIcon} className="w-8" />
      <div className="text-xl">{text}</div>
    </div>
  );
};

type BenefitPointProps = {
  text: string;
};

export default HomePage;
